import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, Popover } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { useWindowSize } from "../../../util/Util";


const POPOVER_CHROME_HEIGHT = 176;

const VIEWPORT_MARGIN = 24;


const MIN_LIST_HEIGHT = 96;

export interface SelectOption {
  id: number;
  name: string;
}

export interface MultiSelectPopoverProps {
  triggerLabel: string;
  options: SelectOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  
  width?: number;
  
  maxListHeight?: number;
  
  triggerHeight?: number;
  searchPlaceholder?: string;
  selectAllLabel?: string;
  resetLabel?: string;
  okLabel?: (count: number) => string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const defaultOkLabel = (count: number) => `Ok (${count} Selected)`;

export const MultiSelectPopover: React.FC<MultiSelectPopoverProps> = ({
  triggerLabel,
  options,
  selectedIds,
  onChange,
  width = 280,
  maxListHeight = 220,
  triggerHeight = 40,
  searchPlaceholder = "Search...",
  selectAllLabel = "Select All",
  resetLabel = "Reset",
  okLabel = defaultOkLabel,
  className,
  style,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [draftIds, setDraftIds] = useState<number[]>(selectedIds);
  const { width: viewportWidth } = useWindowSize();

  const triggerRef = useRef<HTMLDivElement>(null);
  
  const [availableSpace, setAvailableSpace] = useState<number | undefined>();

  
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - VIEWPORT_MARGIN;
      setAvailableSpace(Math.max(spaceBelow, spaceAbove));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open]);

  const listHeight = useMemo(() => {
    if (!availableSpace) return maxListHeight;
    const available = availableSpace - POPOVER_CHROME_HEIGHT;
    return Math.min(maxListHeight, Math.max(MIN_LIST_HEIGHT, available));
  }, [availableSpace, maxListHeight]);

  // Never wider than the viewport on small screens.
  const responsiveWidth = viewportWidth
    ? Math.min(width, viewportWidth - VIEWPORT_MARGIN * 2)
    : width;

  useEffect(() => {
    if (open) {
      setDraftIds(selectedIds);
      setQuery("");
    }
  }, [open, selectedIds]);

  const filtered = useMemo(
    () =>
      options.filter((o) =>
        o.name.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [options, query]
  );

  const draftSet = useMemo(() => new Set(draftIds), [draftIds]);

  const filteredSelectedCount = useMemo(
    () => filtered.reduce((acc, o) => (draftSet.has(o.id) ? acc + 1 : acc), 0),
    [filtered, draftSet]
  );

  const allFilteredChecked =
    filtered.length > 0 && filteredSelectedCount === filtered.length;

  const someFilteredChecked =
    filteredSelectedCount > 0 && filteredSelectedCount < filtered.length;

  const toggleOne = (id: number) => {
    setDraftIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = (e: CheckboxChangeEvent) => {
    const filteredIds = filtered.map((o) => o.id);
    if (e.target.checked) {
      setDraftIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    } else {
      const filteredSet = new Set(filteredIds);
      setDraftIds((prev) => prev.filter((id) => !filteredSet.has(id)));
    }
  };

  const commit = () => {
    onChange(draftIds);
    setOpen(false);
  };

  const content = (
    <div
      style={{
        width: responsiveWidth,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Input
        prefix={
          <SearchOutlined
            style={{ color: "#999" }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 8, flexShrink: 0 }}
        allowClear
      />
      <div
        style={{
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: 8,
          marginBottom: 8,
          flexShrink: 0,
        }}
      >
        <Checkbox
          indeterminate={someFilteredChecked}
          checked={allFilteredChecked}
          onChange={toggleAll}
          disabled={filtered.length === 0}
        >
          {selectAllLabel}
        </Checkbox>
      </div>
      <div
        className="scrollbar-thin"
        style={{
         
          maxHeight: listHeight,
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
         
          scrollbarGutter: "stable",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {filtered.length === 0 ? (
          <div style={{ color: "#999", textAlign: "center", padding: "12px 0" }}>
            No results
          </div>
        ) : (
          filtered.map((opt) => (
            <div key={opt.id}>
              <Checkbox
                checked={draftSet.has(opt.id)}
                onChange={() => toggleOne(opt.id)}
              >
                {opt.name}
              </Checkbox>
            </div>
          ))
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "1px solid #f0f0f0",
          marginTop: 8,
          paddingTop: 8,
          flexShrink: 0,
        }}
      >
        <Button type="link" onClick={() => setDraftIds([])} style={{ padding: 0 }}>
          {resetLabel}
        </Button>
        <Button type="primary" onClick={commit}>
          {okLabel(draftIds.length)}
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(next) => !disabled && setOpen(next)}
      placement="bottomLeft"
      overlayInnerStyle={{ padding: 12 }}
    >
     
      <div ref={triggerRef} style={{ width: "100%" }}>
        <Button
          disabled={disabled}
          className={className}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: triggerHeight,
            ...style,
          }}
        >
          <span>{triggerLabel}</span>
          <DownOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </Button>
      </div>
    </Popover>
  );
};

export default MultiSelectPopover;